/**
 * Pure freeform quick-add parser extracted verbatim from EventListModule
 * (`parseQuickAdd` closure). Turns text like
 *   "dinner with susan 7pm pastis" → { title: "dinner with susan pastis", startAt }
 *
 * The only value the inline closure captured was `calendarDate` (the base date
 * used when the text contains no date token); it is threaded in as an explicit
 * parameter here so the function is pure and unit-testable. Everything else —
 * the time/date regexes, the weekday/month-day/slash-date branches, the
 * past-date → next-year rollover, and the title cleanup — is byte-identical to
 * the original. Behavior is pinned by `parseQuickAdd.char.test.ts`.
 */
export function parseQuickAdd(text: string, calendarDate: Date): { title: string; startAt: Date } {
  const now = new Date();
  let hour = 9, minute = 0; // default 9 AM
  let cleaned = text;

  // Extract time patterns: 7pm, 7:30pm, 14:00, 7 pm
  const timeMatch = cleaned.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i) || cleaned.match(/\b(\d{1,2}):(\d{2})\b/);
  if (timeMatch) {
    hour = parseInt(timeMatch[1], 10);
    minute = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    if (timeMatch[3]) {
      const ampm = timeMatch[3].toLowerCase();
      if (ampm === 'pm' && hour < 12) hour += 12;
      if (ampm === 'am' && hour === 12) hour = 0;
    }
    cleaned = cleaned.replace(timeMatch[0], '').trim();
  }

  // Extract date patterns: "tomorrow", "monday", "apr 5", "4/5"
  const tomorrow = /\btomorrow\b/i;
  const dayName = /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\b/i;
  const monthDay = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+(\d{1,2})\b/i;
  const slashDate = /\b(\d{1,2})\/(\d{1,2})\b/;

  const dayAbbrevMap: Record<string, string> = {
    mon: 'monday', tue: 'tuesday', tues: 'tuesday', wed: 'wednesday',
    thu: 'thursday', thur: 'thursday', thurs: 'thursday', fri: 'friday', sat: 'saturday', sun: 'sunday',
  };

  let date = new Date(calendarDate);
  if (tomorrow.test(cleaned)) {
    date = new Date(now); date.setDate(date.getDate() + 1);
    cleaned = cleaned.replace(tomorrow, '').trim();
  } else if (dayName.test(cleaned)) {
    const match = cleaned.match(dayName)!;
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const fullDay = dayAbbrevMap[match[1].toLowerCase()] || match[1].toLowerCase();
    const target = days.indexOf(fullDay);
    date = new Date(now);
    const diff = (target - date.getDay() + 7) % 7 || 7;
    date.setDate(date.getDate() + diff);
    cleaned = cleaned.replace(match[0], '').trim();
  } else if (monthDay.test(cleaned)) {
    const match = cleaned.match(monthDay)!;
    const months: Record<string, number> = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
    date = new Date(now.getFullYear(), months[match[1].toLowerCase()], parseInt(match[2], 10));
    // Compare date parts only — otherwise "apr 5" typed at 00:01 AM on apr 5
    // would see date (Apr 5 00:00) < now (Apr 5 00:01) and roll to next year.
    const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (date < todayDateOnly) date.setFullYear(date.getFullYear() + 1);
    cleaned = cleaned.replace(match[0], '').trim();
  } else if (slashDate.test(cleaned)) {
    const match = cleaned.match(slashDate)!;
    date = new Date(now.getFullYear(), parseInt(match[1], 10) - 1, parseInt(match[2], 10));
    // Same date-only comparison as month-day path to avoid midnight rollover bug
    const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (date < todayDateOnly) date.setFullYear(date.getFullYear() + 1);
    cleaned = cleaned.replace(match[0], '').trim();
  }

  // Clean up extra whitespace and trailing "at", "on"
  cleaned = cleaned.replace(/\s+(at|on)\s*$/i, '').replace(/\s+/g, ' ').trim();

  date.setHours(hour, minute, 0, 0);
  return { title: cleaned || text, startAt: date };
}
